import { useState } from 'react'
import { CompaniesHeader } from './CompaniesHeader'
import { CompaniesTable } from './CompaniesTable'
import { type GroupOption, type CreateCompanyInput } from '../modals/CreateCompanyModal'
import { type DepositWallet } from '../drawers/DepositWalletsDrawer'
import { type CompanyOption } from '../dialogs/AssignUserToCompanyDialog'
import type { CompaniesFundsFilter } from '../../utils/hooks/useCompaniesFundsFilter'

export interface CompanyData {
  id: string
  company: string
  group: string
  groupId: string
  imageUrl?: string
  groupImageUrl?: string
  tradeType: 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED'
  autoApprove: boolean
  balance: string
  parentCompanyId?: string | null
}

export interface CompaniesTabViewProps {
  companies: CompanyData[]
  loading?: boolean
  // searchTerm: string
  // onSearchChange: (search: string) => void
  title?: string
  showCreateButton?: boolean
  showAssignButton?: boolean
  showWalletsButton?: boolean
  showUnassignButton?: boolean

  // Data for modals
  groups?: GroupOption[]
  allCompanies?: CompanyOption[] // For assign dialog
  depositWallets?: DepositWallet[]
  walletsLoading?: boolean

  // Callbacks
  onCreateCompany?: (input: CreateCompanyInput) => Promise<void>
  onAssignUserToCompany?: (userId: string, companyIds: string[]) => Promise<void>
  onUnassignUserFromCompany?: (userId: string, companyId: string) => Promise<void>
  onViewCompanyDetails?: (companyId: string) => void
  onFetchWallets?: (companyId: string) => void
  onCopyWalletAddress?: (address: string) => void

  // Context data
  userId?: string // For assign/unassign operations
  hideAssignButton?: boolean
  fundsFilter?: CompaniesFundsFilter
  onFundsFilterChange?: (filter: CompaniesFundsFilter) => void
}

export function CompaniesTabView({
  companies,
  loading = false,
  // searchTerm,
  // onSearchChange,
  title = 'Companies',
  showCreateButton = false,
  showAssignButton = false,
  showWalletsButton = false,
  showUnassignButton = false,
  groups = [],
  allCompanies = [],
  depositWallets = [],
  walletsLoading = false,
  onCreateCompany,
  onAssignUserToCompany,
  onUnassignUserFromCompany,
  onViewCompanyDetails,
  onFetchWallets,
  onCopyWalletAddress,
  userId,
  hideAssignButton = false,
  fundsFilter = 'all',
  onFundsFilterChange,
}: CompaniesTabViewProps) {
  const [modalHandlers, setModalHandlers] = useState<{
    handleCreateClick: () => void
    handleAssignClick: () => void
  } | null>(null)

  return (
    <div className="space-y-4">
      <CompaniesHeader
        title={title}
        // searchTerm={searchTerm}
        // onSearchChange={onSearchChange}
        showCreateButton={showCreateButton}
        showAssignButton={showAssignButton}
        onCreateClick={modalHandlers?.handleCreateClick}
        onAssignClick={modalHandlers?.handleAssignClick}
        hideAssignButton={hideAssignButton}
        fundsFilter={fundsFilter}
        onFundsFilterChange={onFundsFilterChange}
      />

      <CompaniesTable
        companies={companies}
        loading={loading}
        showCreateButton={showCreateButton}
        showAssignButton={showAssignButton}
        showWalletsButton={showWalletsButton}
        showUnassignButton={showUnassignButton}
        groups={groups}
        allCompanies={allCompanies}
        depositWallets={depositWallets}
        walletsLoading={walletsLoading}
        onCreateCompany={onCreateCompany}
        onAssignUserToCompany={onAssignUserToCompany}
        onUnassignUserFromCompany={onUnassignUserFromCompany}
        onViewCompanyDetails={onViewCompanyDetails}
        onFetchWallets={onFetchWallets}
        onCopyWalletAddress={onCopyWalletAddress}
        userId={userId}
        onModalHandlersReady={setModalHandlers}
      />
    </div>
  )
}
